import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plane, Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome to VoyageOS');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      {/* Left: Hero Image */}
      <div
        className="hidden md:flex relative items-end overflow-hidden"
        style={{
          backgroundImage: `url(${process.env.REACT_APP_HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative z-10 p-10 pb-14 text-white space-y-4 max-w-lg">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-tight tracking-tight">
            Manage your travel business with intelligence
          </h2>
          <p className="text-base text-white/80 leading-relaxed">
            AI-powered itineraries, automated follow-ups, and smart CRM — all in one platform.
          </p>
          <div className="flex gap-8 pt-2">
            <div>
              <div className="text-2xl font-heading font-bold text-voyage-orange">500+</div>
              <div className="text-xs text-white/60 mt-0.5">Agencies trust us</div>
            </div>
            <div>
              <div className="text-2xl font-heading font-bold text-voyage-orange">98%</div>
              <div className="text-xs text-white/60 mt-0.5">Faster lead capture</div>
            </div>
            <div>
              <div className="text-2xl font-heading font-bold text-voyage-orange">3x</div>
              <div className="text-xs text-white/60 mt-0.5">Revenue growth</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="flex items-center justify-center p-8 md:p-12 bg-background">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="w-11 h-11 rounded-xl gradient-blue flex items-center justify-center shadow-lg shadow-voyage-blue/25">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-2xl font-bold tracking-tight text-foreground">
                VoyageOS
              </span>
            </div>
            <div className="pt-4">
              <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">
                Welcome back
              </h1>
              <p className="text-muted-foreground text-sm mt-2 leading-relaxed">
                Sign in to your travel agency dashboard
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@voyageos.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10 bg-card border-border focus-visible:ring-2 focus-visible:ring-voyage-blue rounded-lg"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-voyage-blue hover:underline font-medium" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 gradient-blue hover:opacity-90 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-voyage-blue/20"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  Sign In <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="pt-4 border-t border-border">
            <p className="text-sm text-center text-muted-foreground">
              New travel agency?{' '}
              <Link to="/signup" className="text-voyage-blue hover:underline font-semibold" data-testid="goto-signup-link">
                Register your agency
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
